import { promises as fs } from 'fs';
import { join } from 'path';
import { FileSystemError, ValidationError } from '@/types';

/**
 * Validate that the JSON report exists and is parsable.
 */
export async function validateJsonReport(jsonPath: string): Promise<void> {
  try {
    const stat = await fs.stat(jsonPath);
    if (!stat.isFile()) {
      throw new ValidationError(`JSON report path is not a file: ${jsonPath}`);
    }
  } catch (error) {
    if (error instanceof ValidationError) throw error;
    throw new FileSystemError(
      `JSON report not found: ${jsonPath}`,
      error as Error
    );
  }

  try {
    const content = await fs.readFile(jsonPath, 'utf-8');
    JSON.parse(content);
  } catch (error) {
    if (error instanceof SyntaxError) {
      throw new ValidationError(
        `Failed to parse JSON report: ${(error as Error).message}`
      );
    }
    throw error as Error;
  }
}

/**
 * Validate that the HTML report directory exists and contains index.html.
 */
export async function validateHtmlReportDir(htmlDir: string): Promise<void> {
  try {
    const stat = await fs.stat(htmlDir);
    if (!stat.isDirectory()) {
      throw new ValidationError(
        `HTML report path is not a directory: ${htmlDir}`
      );
    }
  } catch (error) {
    throw new FileSystemError(
      `HTML report directory not found: ${htmlDir}`,
      error as Error
    );
  }

  const indexPath = join(htmlDir, 'index.html');
  try {
    const stat = await fs.stat(indexPath);
    if (!stat.isFile()) {
      throw new ValidationError(
        `index.html not found in HTML report directory: ${htmlDir}`
      );
    }
  } catch {
    throw new ValidationError(
      `index.html not found in HTML report directory: ${htmlDir}`
    );
  }
}

/**
 * Validate that the trace directory exists.
 */
export async function validateTraceDir(traceDir: string): Promise<void> {
  try {
    const stat = await fs.stat(traceDir);
    if (!stat.isDirectory()) {
      throw new ValidationError(`Trace path is not a directory: ${traceDir}`);
    }
  } catch (error) {
    throw new FileSystemError(
      `Trace directory not found: ${traceDir}`,
      error as Error
    );
  }
}
